import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Shield, Globe, Loader2, Users, Building2, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const states = [
  { code: 'NY', name: 'New York' },
  { code: 'CA', name: 'California' },
  { code: 'TX', name: 'Texas' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'MI', name: 'Michigan' },
  { code: 'WV', name: 'West Virginia' }
];

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'ar', name: 'العربية' },
  { code: 'zh', name: '繁體中文' }
];

const userTypes = [
  { 
    value: 'community_member', 
    label: { en: 'Community Member', es: 'Miembro de la Comunidad' },
    description: { en: 'Individual advocate or concerned citizen', es: 'Defensor individual o ciudadano preocupado' },
    icon: User
  },
  { 
    value: 'community_org', 
    label: { en: 'Community Organization', es: 'Organización Comunitaria' },
    description: { en: 'Non-profit, advocacy group, or community organization', es: 'ONG, grupo de defensa u organización comunitaria' },
    icon: Users
  },
  { 
    value: 'corporate_esg', 
    label: { en: 'Corporate ESG Team', es: 'Equipo ESG Corporativo' },
    description: { en: 'Corporate sustainability, compliance, or ESG team', es: 'Equipo de sostenibilidad, cumplimiento o ESG corporativo' },
    icon: Building2
  }
];

const Register = () => {
  const { login } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    organization_name: '',
    company_name: '',
    phone: '',
    state: 'NY',
    language: language,
    user_type: 'community_member',
    interests: []
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleStateChange = (value) => {
    setFormData(prev => ({
      ...prev,
      state: value
    }));
  };

  const handleUserTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      user_type: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        ...formData,
        language: language
      });
      const { access_token, user } = response.data;
      login(user, access_token);
      
      // Seed demo data for new users
      try {
        await axios.post(`${API}/seed-data`);
      } catch (seedError) {
        // Ignore if data already seeded
      }
      
      toast.success(language === 'es' ? '¡Cuenta creada exitosamente!' : 'Account created successfully!');
      
      // Navigate based on user type
      if (formData.user_type === 'corporate_esg') {
        navigate('/corporate');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'es' ? 'Error al registrarse' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const showOrgField = formData.user_type === 'community_org';
  const showCompanyField = formData.user_type === 'corporate_esg';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Shield className="h-8 w-8 text-primary" />
          <span className="font-bold text-xl tracking-tight">PoliBot</span>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language.toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {languages.map(lang => (
              <DropdownMenuItem 
                key={lang.code}
                onClick={() => changeLanguage(lang.code)} 
                className={language === lang.code ? 'bg-muted' : ''}
              >
                {lang.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg animate-scale-in">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">{t('auth.registerTitle')}</CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Únete a la comunidad de justicia ambiental'
                : 'Join the environmental justice community'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* User Type Selection */}
              <div className="space-y-3">
                <Label>{language === 'es' ? 'Tipo de Cuenta' : 'Account Type'}</Label>
                <RadioGroup 
                  value={formData.user_type} 
                  onValueChange={handleUserTypeChange}
                  className="grid gap-3"
                >
                  {userTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <div 
                        key={type.value}
                        className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.user_type === type.value 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => handleUserTypeChange(type.value)}
                      >
                        <RadioGroupItem value={type.value} id={type.value} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={type.value} className="flex items-center gap-2 cursor-pointer font-medium">
                            <Icon className="h-4 w-4" />
                            {type.label[language]}
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {type.description[language]}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </RadioGroup>
              </div>

              {/* Organization Name (for community orgs) */}
              {showOrgField && (
                <div className="space-y-2 animate-fade-in">
                  <Label htmlFor="organization_name">{t('auth.orgName')}</Label>
                  <Input
                    id="organization_name"
                    name="organization_name"
                    type="text"
                    placeholder={language === 'es' ? 'Nombre de tu organización' : 'Your organization name'}
                    value={formData.organization_name}
                    onChange={handleChange}
                    required
                    data-testid="org-name-input"
                  />
                </div>
              )}

              {/* Company Name (for corporate) */}
              {showCompanyField && (
                <div className="space-y-2 animate-fade-in">
                  <Label htmlFor="company_name">{language === 'es' ? 'Nombre de la Empresa' : 'Company Name'}</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    placeholder={language === 'es' ? 'Nombre de tu empresa' : 'Your company name'}
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    data-testid="company-name-input"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.org"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">{t('auth.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="phone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('auth.selectState')}</Label>
                <Select value={formData.state} onValueChange={handleStateChange}>
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder={t('auth.selectState')} />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map(state => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {language === 'es' ? 'Creando cuenta...' : 'Creating account...'}
                  </>
                ) : (
                  t('nav.register')
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-muted-foreground">
              {t('auth.haveAccount')}{' '}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                {t('nav.login')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Register;
